import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    clearFilters,
    updateCheckboxes,
    setCheckboxes,
    getCheckboxState,
    CategoryState,
    getSelectedCategories,
    getCategoryState,
    getCategories,
    addCategoriesSelection,
    searchCategories,
    getCountries,
} from '@emi/shared/state/filters';
import { Category, CheckboxState, CheckboxTreeNode } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-categories',
    templateUrl: './filter-categories.component.html',
    styleUrls: ['./filter-categories.component.scss'],
})
export class FilterCategoriesComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public onApplyClicked = new EventEmitter();

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    private categoriesSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public categoriesTree: Array<CheckboxTreeNode> = [];
    public stateCategories: Array<Category> = [];
    public selectedCategories: Array<Category> = [];
    public lowestLevelTooltip = Constants.Tooltips.LOWEST_LEVEL_CATEGORIES;
    public loading: boolean;
    public showError = false;
    public errorMessage = '';
    public searchText: string | null;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    public hasSingleTopLevelCategory = false;
    public noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;

    constructor(private readonly store: Store<CategoryState>) {}

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.categoriesSubscription = this.store.pipe(select(getSelectedCategories)).subscribe((categories) => {
            if (categories.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: this.getSelectedCategories(categories),
                        })
                    );
                }
                this.selectedCategories = categories;
                this.categoriesTree = categories.map((category) => {
                    return this.buildCheckboxTreeNode(category, this.checkedArray);
                });
            }
        });

        this.stateSubscription = this.store.pipe(select(getCategoryState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.searchText = state.searchText;
            this.hasSingleTopLevelCategory = state.categories.length < 2; // if subscriber only has 1 top level category then category tree is fully expanded

            if (this.selectedCategories.length === 0) {
                this.categoriesTree = this.searchText
                    ? state.searchedCategories.map((category) => {
                          return this.buildCheckboxTreeNode(category, this.checkedArray);
                      })
                    : state.categories.map((category) => {
                          return this.buildCheckboxTreeNode(category, this.checkedArray);
                      });
            }
            this.eventsSubject.next(this.categoriesTree);
            this.stateCategories = state.categories;
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const categories: Array<Category> = (this.filterCategories(this.stateCategories, this.checkedArray) ?? [])
                .map((stateCategory) => this.buildCheckboxTreeNode(stateCategory, this.checkedArray))
                .map((category) => this.buildCategory(category));
            this.store.dispatch(addCategoriesSelection({ categories }));
            this.store.dispatch(getCountries());
            if(this.stateCategories.length === 0){
                this.store.dispatch(clearFilters());
            } 
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedCategories.map((categories) => {
                            return this.buildCheckboxTreeNode(categories, this.getSelectedCategories(this.selectedCategories));
                        }),
                    })
                );
                const currentSelectableCategories = this.selectedCategories.length > 0 ? this.selectedCategories : this.stateCategories;
                this.categoriesTree = currentSelectableCategories.map((category) => {
                    return this.buildCheckboxTreeNode(category, this.getSelectedCategories(this.selectedCategories));
                });
                this.eventsSubject.next(this.categoriesTree);
            });
        }

        if (this.selectedCategories.length === 0 && this.stateCategories.length === 0) {
            this.store.dispatch(getCategories());
        }
    }

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.getChildCategories([selectedCheckbox], selectedCheckbox.state).map((checkbox) => {
            this.store.dispatch(updateCheckboxes({ checkbox: checkbox }));
        });
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(
            this.getSelectedCategories(this.selectedCategories).length !== this.checkedArray.length && this.checkedArray.length > 0
        );
    }

    public buildCategory(node: CheckboxTreeNode, breadcrumb?: string): Category {
        if (!node.children || node.children.length === 0) {
            return new Category(node.id, node.label, breadcrumb, undefined, undefined, node.industryCode);
        }

        const childCategories = [];
        for (const child of node.children) {
            if (breadcrumb) {
                childCategories.push(this.buildCategory(child, `${breadcrumb} / ${node.label}`));
            } else {
                childCategories.push(this.buildCategory(child, node.label));
            }
        }

        return new Category(node.id, node.label, breadcrumb, childCategories, undefined, node.industryCode);
    }

    public buildCheckboxTreeNode(category: Category, selectedCategories: CheckboxTreeNode[]): CheckboxTreeNode {
        let checkboxState: CheckboxState = selectedCategories.some((selectedCategory) => selectedCategory.id === category.id)
            ? 'checked'
            : 'unchecked';

        if (!category.childCategories || category.childCategories.length === 0) {
            return new CheckboxTreeNode(
                category.id,
                category.name,
                checkboxState,
                undefined,
                undefined,
                undefined,
                undefined,
                category.industryCode
            );
        }

        let children = [];
        for (const childCategory of category.childCategories) {
            children.push(this.buildCheckboxTreeNode(childCategory, selectedCategories));
        }

        const parentState: CheckboxState = children.every((x) => x.state === 'checked') ? 'checked' : 'unchecked';

        //  Business logic: Expand if search text exists. Expand categories once user has applied selections. Expand top level category if user has single industry.
        const expandCategory =
            (this.searchText && this.searchText?.length > 0) ||
            selectedCategories.length > 0 ||
            (this.hasSingleTopLevelCategory && category.level == 0);

        return new CheckboxTreeNode(
            category.id,
            category.name,
            parentState,
            expandCategory,
            children,
            undefined,
            undefined,
            category.industryCode
        );
    }

    private filterCategories(categories: Category[], selectedCategories: CheckboxTreeNode[]): Category[] | null {
        const relevantCategories = [];
        for (const category of categories) {
            if (
                selectedCategories.some(
                    (selectedCategory) => selectedCategory.id === category.id && selectedCategory.industryCode == category.industryCode
                )
            ) {
                relevantCategories.push(category);
            } else if (category.childCategories) {
                const relevantChildren = this.filterCategories(category.childCategories, selectedCategories);
                if (relevantChildren) {
                    let newCategory = { ...category, childCategories: relevantChildren };
                    relevantCategories.push(newCategory);
                }
            }
        }
        return relevantCategories.length > 0 ? relevantCategories : null;
    }

    public getSelectedCategories(categoryTree: Array<Category>): Array<CheckboxTreeNode> {
        let selections: Array<CheckboxTreeNode> = [];
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                selections.push(
                    new CheckboxTreeNode(
                        category.id,
                        category.name,
                        'checked',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        category.industryCode
                    )
                );
            } else {
                selections.push(...this.getSelectedCategories(category.childCategories));
            }
        }
        return selections;
    }

    public getChildCategories(categoryTree: Array<CheckboxTreeNode>, state: CheckboxState): Array<CheckboxTreeNode> {
        let selections: Array<CheckboxTreeNode> = [];
        for (const category of categoryTree) {
            if (!category.children || category.children.length === 0) {
                selections.push(
                    new CheckboxTreeNode(
                        category.id,
                        category.label,
                        state,
                        category.isExpanded,
                        category.children,
                        category.parentId,
                        undefined,
                        category.industryCode
                    )
                );
            } else {
                selections.push(...this.getChildCategories(category.children, state));
            }
        }
        return selections;
    }

    public searchFiltersByText(text: string): void {
        this.store.dispatch(searchCategories({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.store.dispatch(searchCategories({ searchText: '', delay: 0 }));
    }

    public ngOnDestroy(): void {
        if (this.searchText) {
            this.store.dispatch(searchCategories({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.categoriesSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }
}
